import { FC, useEffect, useState } from 'react';
import { Card, Form, Select, Radio, Input } from 'antd';
import { InfoCircleOutlined } from "@ant-design/icons";
import { CONTROL_TYPE } from 'constants/Constants';
import './sectionStep.scss';

const { TextArea } = Input;
const isValidInput = (value: string) => {
  // Disallow special characters except letters, numbers, spaces, underscores, and hyphens
  const regex = /^[a-zA-Z0-9 _-]*$/;
  return regex.test(value);
};

interface IProps {
  name: string;
  controlType: string;
  options?: any;
  required?: boolean;
  tooltip?: string;
  pattern?: string;
  placeholder?: string;
  readonly?: boolean;
  selectedValue?: string;
  hidden?: boolean;
  message?: string;
}
interface ISectionTitle {
  sectionTitle: string;
  subsections: any;
  onChange: (
    section: string,
    subsection: string,
    name: string,
    val: any,
    options?: any
  ) => void;
  form: any;
  scrollRef: any;
  sectionIndex: number;
}

const SectionStep: FC<ISectionTitle> = (properties: ISectionTitle) => {
  const { sectionTitle, subsections, onChange, form, scrollRef, sectionIndex } =
  properties;
  const [error, setError] = useState('');
  
  const getControlType = (section: string, subsection: string, val: any) => {
    const {
      name,
      controlType,
      options,
      placeholder,
      selectedValue,
      readonly = false,
    } = val;
    
    if (selectedValue) {
      onChange(section, subsection, name, selectedValue, options);
    }
    switch (controlType) {
      case CONTROL_TYPE.DROPDOWN: {
        return (
          <Select
          placeholder={placeholder ? placeholder : "Select"}
          disabled={readonly}
          onChange={(value: any) =>
            onChange(section, subsection, name, value, options)
          }
          defaultValue={selectedValue}
          >
            {options &&
              options.length > 0 &&
              options.map((item: any) => (
                <Select.Option value={item.value}> {item.value} </Select.Option>
              ))}
          </Select>
        );
      }
      case CONTROL_TYPE.RADIO: {
        return (
          <Radio.Group
          disabled={readonly}
          onChange={(e: any) =>
            onChange(section, subsection, name, e.target.value, options)
          }
          defaultValue={selectedValue}
          >
            {options &&
              options.length > 0 &&
              options.map((item: any) => (
                <Radio value={item.value}>{item.value}</Radio>
              ))}
          </Radio.Group>
        );
      }
      case CONTROL_TYPE.TEXTAREA: {
        return (
          <TextArea
            placeholder={placeholder ? placeholder : "Enter"}
            disabled={readonly}
            rows={val?.rows}
            onChange={(e: any) =>
              onChange(section, subsection, name, e.target.value)
            }
            defaultValue={selectedValue}
          />
        );
      }
      case CONTROL_TYPE.PASSWORD: {
        return (
        <>
          <Form.Item
          validateStatus={error && subsection === 'mongo' ? 'error' : ''}
          help={subsection === 'mongo'? error : ''}
          >
          <Input.Password
            placeholder={placeholder ? placeholder : "Enter"}
            disabled={readonly}
            onChange={(e: any) =>{

              if(section === 'Database' && subsection === 'mongo'){
                  if (isValidInput(e.target.value)) {
                    onChange(section, subsection, name, e.target.value)
                    setError('');
                } else {
                    setError('Special characters are not allowed.');
                }
              } else {
                onChange(section, subsection, name, e.target.value)
              }
             }
            }
            defaultValue={selectedValue}
          />
          </Form.Item>
          </>
        );
      }
      default:
        return (
          <Input
            placeholder={placeholder ? placeholder : "Enter"}
            disabled={readonly}
            onChange={(e: any) =>
              onChange(section, subsection, name, e.target.value)
            }
            defaultValue={selectedValue}
          />
        );
    }
  };

  const toUpperCase = (val: string) => {
    return val?.charAt(0)?.toUpperCase() + val?.slice(1);
  };

  const getRef = (index: number, ref: any) => {
    if (!scrollRef.current[sectionIndex]) {
      scrollRef.current[sectionIndex] = [];
    }
    scrollRef.current[sectionIndex][index] = ref;
  };

  const getMessage = (prop: any) => {    
    if (prop?.message) {
      return prop?.message;
    } else if (prop?.controlType === CONTROL_TYPE.RADIO) {
      const selectedOption = prop.options?.find(
        (option: any) => option.value === prop.selectedValue
      );
      return selectedOption?.message;
    }
    return "";
  };

  return (
    <div className="section-wrapper">
      {Object.entries(subsections)?.map((subsection: any, index: number) => {
        const [subsectionTitle, props] = subsection;
        return (
          <div ref={(r) => getRef(index, r)}>
            <Card className="card">
              <div className="card-title">{toUpperCase(subsectionTitle)}</div>
              <br />
              <Form
                form={form}
                labelCol={{ xs: 24, sm: 24, md: 8, lg: 8, xl: 8 }}
                wrapperCol={{ xs: 24, sm: 24, md: 16, lg: 16, xl: 16 }}
              >
                {props?.props?.map((val: IProps) => (
                  <>
                    {!val?.hidden ? (
                      <div id={`${sectionTitle}${subsectionTitle}${val?.name}`}>
                        <Form.Item
                          name={`${sectionTitle}${subsectionTitle}${val?.name}`}
                          className="form-field"
                          label={
                            <div className="label">
                              {toUpperCase(val?.name)}
                            </div>
                          }
                          colon={false}
                          rules={[
                            {
                              required: val?.required ? true : false,
                              message: "Field is Required!",
                            },
                            {
                              pattern: val?.pattern
                                ? new RegExp(val?.pattern)
                                : undefined,
                              message: "Please enter valid data",
                            },
                          ]}
                          validateStatus={
                            getMessage(val) ? "warning" : "validating"
                          }
                          help={getMessage(val)}
                          tooltip={{
                            title: val?.tooltip ? val?.tooltip : "",
                            icon: <InfoCircleOutlined />,
                            placement: "top",
                          }}
                        >
                          {getControlType(sectionTitle, subsectionTitle, val)}
                        </Form.Item>
                      </div>
                    ) : (
                      ""
                    )}
                  </>
                ))}
              </Form>
            </Card>
          </div>
        );
      })}
    </div>
  );
};

export default SectionStep;